import React, { useState, useContext } from 'react';
import { Helmet } from 'react-helmet';
import { useApolloClient } from '@apollo/client';
import { Box, Button, Text, FormField } from '@indeed/ifl-components';
import { usePageFlow } from '../../../lib/pageFlow/usePageFlow';
import {
    useGetDateOfBirthFormDataQuery,
    useUpdateLightApplyDateOfBirthMutation,
} from './__generated__/DateOfBirthPage.generated';
import {
    updateSmartApplyContactInfoCache,
    buildCompleteContactInfoMutationInput,
    validateCacheServerConsistency,
} from '../../../lib/apollo/smartApplyStateCache';
import PageTransition from '../../../components/PageTransition/PageTransition';
import FormHeader from '../components/FormHeader/FormHeader';
import DateSelect from '../components/DateSelect';
import { NodeIdContext } from '../../../lib/context/NodeIdContext';
import { useSearchParams } from 'react-router-dom';

/**
 * DateOfBirthPage with Reactive Apollo Cache Updates
 *
 * This approach:
 * 1. ✅ Writes directly to SmartApplyApplicationState cache (no separate local state)
 * 2. ✅ Handles dateOfBirth field with YYYY-MM-DD format conversion
 * 3. ✅ Single server mutation on "Continue" (bulk save)
 * 4. ✅ Zero useState complexity for Apollo data
 * 5. ✅ Uses OneGraph schema as the single source of truth
 * 6. ✅ Automatic UI reactivity via Apollo cache normalization (__typename fields)
 */
const DateOfBirthPage: React.FC = () => {
    const { goForward, loading: pageFlowLoading } = usePageFlow();
    const apolloClient = useApolloClient();

    const [searchParams] = useSearchParams();
    const indeedApplyableJobId = searchParams.get('indeedApplyableJobId') || '';
    // Get nodeId from context
    const nodeId = useContext(NodeIdContext);

    // 1. Get server data (SmartApply + ApplyParameters date of birth config) - Apollo should auto-update on cache changes
    const { data, loading } = useGetDateOfBirthFormDataQuery({
        variables: {
            nodeId: nodeId || '', // Provide empty string as fallback for TypeScript
            indeedApplyableJobId, // Pass job ID to get date of birth configuration from applyParameters
        },
        skip: !nodeId || !indeedApplyableJobId, // Skip query when either parameter is missing
        errorPolicy: 'all',
        // Cache-first: return cache data immediately, only fetch if not in cache
        fetchPolicy: 'cache-first',
    });

    // 2. Server mutation for bulk save on Continue
    const [updateServerDateOfBirth, { loading: saving }] = useUpdateLightApplyDateOfBirthMutation();

    // 3. Extract current state from Apollo data - should auto-update when data changes
    const smartApplyState =
        data?.smartApplyApplicationState?.__typename === 'SmartApplyApplicationState'
            ? data.smartApplyApplicationState
            : null;

    const contactInfo = smartApplyState?.contactInfo;

    // For date of birth, assume it's required by default
    const isDobRequired = true; // Default behavior for date of birth

    // 4. Get current dateOfBirth value from Apollo query result (YYYY-MM-DD format)
    const currentDateOfBirth = contactInfo?.dateOfBirth || '';

    // State to track when user has attempted to continue (for validation UX)
    const [hasAttemptedContinue, setHasAttemptedContinue] = useState(false);

    const handleDateChange = (dateValue: string) => {
        // Prevent cache updates while data is still loading
        if (loading) {
            return;
        }

        if (!smartApplyState?.id) {
            console.warn('No SmartApplyApplicationState ID available for cache update');
            return;
        }

        // Use cache.modify() - more flexible, no fragment validation issues
        const updatedContactInfo = updateSmartApplyContactInfoCache(
            apolloClient.cache,
            smartApplyState.id,
            { dateOfBirth: dateValue },
        );

        if (!updatedContactInfo) {
            console.error('Failed to update dateOfBirth');
        }
    };

    // 5. Handle Continue - flush all changes to server
    const handleContinue = async () => {
        if (!smartApplyState?.id || !contactInfo) {
            console.error('Cannot save: missing SmartApplyApplicationState ID or contact info');
            return;
        }

        // Validation - require date of birth based on standard behavior
        const hasRequiredFields = !!(
            !isDobRequired ||
            (currentDateOfBirth && currentDateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/))
        );

        if (!hasRequiredFields) {
            // Show validation errors now that user has attempted to continue
            setHasAttemptedContinue(true);
            return;
        }

        // ✅ CRITICAL: Build complete mutation input to prevent server data loss
        // The server does NOT support partial updates - must include ALL contactInfo fields
        const completeMutationInput = buildCompleteContactInfoMutationInput(
            contactInfo, // Complete current contactInfo from cache
            {
                // Only the field we want to update (but all existing fields preserved)
                dateOfBirth: currentDateOfBirth,
            },
        );

        const mutationVariables = {
            input: { id: smartApplyState.id },
            updateContactInfoInput: completeMutationInput,
        };

        try {
            // Send complete contactInfo to server (all fields included)
            await updateServerDateOfBirth({
                variables: mutationVariables,
                // ✅ Validate that server preserved all data
                update: (cache, { data: mutationData }) => {
                    if (mutationData?.smartApplyApplicationState?.updateContactInfo?.state) {
                        const serverContactInfo =
                            mutationData.smartApplyApplicationState.updateContactInfo.state;

                        // ✅ Validate that server returned ALL fields we sent (data preservation check)
                        validateCacheServerConsistency(
                            completeMutationInput,
                            serverContactInfo,
                            'UpdateLightApplyDateOfBirth',
                        );

                        console.log(
                            '✅ Date of birth successfully saved with complete data preservation',
                        );
                        void goForward();
                    }
                },
            });
        } catch (error) {
            console.error('Failed to save date of birth:', error);
            // Could show error message to user
        }
    };

    // 6. Form validation and dirty state from cache
    const canContinue = !!(
        !isDobRequired ||
        (currentDateOfBirth && currentDateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/))
    );

    if (loading) {
        return (
            <Box sx={{ padding: '32px 16px', textAlign: 'center' }}>
                <Text>プロフィール情報を読み込んでいます...</Text>
            </Box>
        );
    }

    return (
        <PageTransition pageKey="contact-info-dob">
            <Helmet>
                <title>生年月日を入力してください - Light Apply - Indeed</title>
            </Helmet>

            <Box sx={{ inlineSize: '100%' }}>
                <FormHeader step={3} total={5} title="生年月日を入力してください" />

                {/* Form Fields - Reading from SmartApplyApplicationState cache */}
                <Box
                    sx={{
                        marginBlockEnd: '32px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '32px',
                    }}
                >
                    <Box sx={{ marginBlockEnd: '24px', inlineSize: '100%' }}>
                        <FormField
                            label="生年月日"
                            showRequiredIndicator={isDobRequired}
                            helperText="満16歳に達してから最初の3月31日を経過していない場合は、このサイトを利用できません。"
                            errorText={
                                hasAttemptedContinue &&
                                isDobRequired &&
                                (!currentDateOfBirth ||
                                    !currentDateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/))
                                    ? '年月日をすべて選択してください'
                                    : ''
                            }
                            invalid={
                                hasAttemptedContinue &&
                                isDobRequired &&
                                (!currentDateOfBirth ||
                                    !currentDateOfBirth.match(/^\d{4}-\d{2}-\d{2}$/))
                            }
                        >
                            <DateSelect value={currentDateOfBirth} onChange={handleDateChange} />
                        </FormField>
                    </Box>

                    {/* Continue Button - Bulk Save to Server */}
                    <Box>
                        <Button
                            variant="primary"
                            size="lg"
                            sx={{ inlineSize: '100%' }}
                            onClick={handleContinue}
                            disabled={!canContinue || saving || pageFlowLoading}
                        >
                            {saving ? '保存中...' : pageFlowLoading ? '読み込み中...' : '次へ'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </PageTransition>
    );
};

export default DateOfBirthPage;
